import { useEffect } from 'react';
import type { Command, Unit } from '@/game/sim';
import { tickWorkers } from '@/game/view/tickCache';
import { intentReader, isWorker, pinCommands } from '@/game/view/workerIntent';
import { useSimFrame } from '../app/simFrame';
import type { UiStore } from '../ui/store';
import { WORKERS_MIME } from '../ui/store';

/** Intent lines listed before "+N more". */
const MAX_LINES = 4;

/**
 * What the selected workers are up to: a line each ("Wk 4 Gathering food 📌
 * home: City 1 → Farm (4/5)", violet when it works for another city than its
 * home) and Pinned / Auto chips for the selection. The lines are also the drag
 * handle for re-homing: drop them on a City chip (`CityChips`).
 */
export function WorkerIntents({
  dispatch,
  ui,
  selectedUnits,
  omitJob = false,
}: {
  dispatch: (c: Command) => void;
  ui: UiStore;
  selectedUnits: readonly Unit[];
  /** Leave the job out of each line (a lone unit's info panel says it on its task line). */
  omitJob?: boolean;
}) {
  // A drag that ends anywhere (or whose source goes away) must not leave the
  // City chips offering a drop.
  useEffect(() => {
    const clear = () => ui.set({ draggingWorkers: null });
    window.addEventListener('dragend', clear, true);
    window.addEventListener('drop', clear, true);
    return () => {
      window.removeEventListener('dragend', clear, true);
      window.removeEventListener('drop', clear, true);
      clear();
    };
  }, [ui]);
  const frame = useSimFrame();
  const { view: state, playerId, rules, terrain, cityNumbers } = frame;
  const workers = selectedUnits.filter((u) => isWorker(rules, u, playerId));
  if (workers.length === 0) return null;
  const ctx = { state, rules, terrain, playerId, cityNumbers, index: tickWorkers(state, rules) };
  const read = intentReader(ctx);
  const intents = workers.flatMap((u) => read(u) ?? []);
  const pins = pinCommands(ctx, workers);
  const pinnedCount = intents.filter((i) => i.pinned).length;
  const ids = workers.map((u) => u.id);
  return (
    <div className="flex w-full min-w-0 flex-wrap items-start gap-2">
      {/* biome-ignore lint/a11y/noStaticElementInteractions: pointer-only drag handle; the chips beside it are the keyboard route to Auto */}
      <div
        draggable
        title="Drag onto a City chip to make it their home"
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData(WORKERS_MIME, ids.join(','));
          ui.set({ draggingWorkers: ids });
        }}
        onDragEnd={() => ui.set({ draggingWorkers: null })}
        className="min-w-0 flex-1 cursor-grab space-y-0.5 rounded-lg bg-fill px-2 py-1 tabular-nums active:cursor-grabbing"
      >
        {intents.slice(0, MAX_LINES).map((i) => (
          <p
            key={i.id}
            className={`truncate ${i.visiting ? 'text-visitor' : 'text-fg-muted'}`}
            title={i.visiting ? 'Working for a city other than its home' : undefined}
          >
            <span className="font-medium text-fg">{i.label}</span>
            {omitJob ? '' : ` ${i.verb}`}
            {i.pinned && <span className="mx-0.5">📌</span>}
            {i.home ? ` home: ${i.home}` : ''}
            {!omitJob && i.where ? ` → ${i.where}` : ''}
          </p>
        ))}
        {intents.length > MAX_LINES && (
          <p className="text-fg-subtle">+{intents.length - MAX_LINES} more</p>
        )}
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={pins.length === 0}
          aria-pressed={pinnedCount === intents.length}
          title="Hold each worker on the job it is on now: automation leaves it there"
          onClick={() => {
            for (const command of pins) dispatch(command);
          }}
          className={`chip ${pinnedCount === intents.length ? 'chip-selected' : ''}`}
        >
          📌 Pinned
        </button>
        <button
          type="button"
          disabled={pinnedCount === 0}
          aria-pressed={pinnedCount === 0}
          title="Let automation choose their work again"
          onClick={() => dispatch({ type: 'automate', unitIds: ids })}
          className={`chip ${pinnedCount === 0 ? 'chip-selected' : ''}`}
        >
          Auto
        </button>
      </div>
    </div>
  );
}
